import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, Info, X } from "lucide-react";
import { ChapterHeading, Reveal } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
const num = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 });
const WEEKS_MONTH = 4.33;

function useAnimatedNumber(value, duration = 500) {
  const [display, setDisplay] = useState(value);
  const prev = useRef(value);
  useEffect(() => {
    const from = prev.current;
    const to = value;
    if (from === to || !isFinite(to)) return;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const e = 1 - Math.pow(1 - p, 3);
      setDisplay(from + (to - from) * e);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    prev.current = value;
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return display;
}

const FIELDS = [
  { key: "appts", label: "Agendamentos", sub: "por dia", min: 1, max: 300 },
  { key: "minutes", label: "Tempo médio por agendamento", min: 1, max: 30, unit: "min" },
  { key: "days", label: "Dias de funcionamento", min: 3, max: 7, unit: "/semana" },
  { key: "cost", label: "Custo da hora da equipe", min: 10, max: 100, unit: "R$/h" },
];

const Field = ({ def, value, onChange }) => {
  const disp = Math.round(value);
  const fill = Math.min(100, Math.max(0, ((disp - def.min) / (def.max - def.min)) * 100));

  const handle = (raw) => {
    if (isNaN(raw)) return;
    onChange(def.key, Math.min(def.max, Math.max(def.min, raw)));
  };

  return (
    <div>
      <div className="mb-2.5 flex items-end justify-between gap-3">
        <label htmlFor={`calc-field-${def.key}-input`} className="text-sm font-medium text-[var(--muted)]">
          {def.label}
          {def.sub && <span className="block text-[11px] font-normal text-[var(--brand)]">{def.sub}</span>}
        </label>
        <div className="flex items-center gap-1.5">
          <input
            id={`calc-field-${def.key}-input`}
            data-testid={`calc-field-${def.key}-input`}
            type="number"
            min={def.min}
            max={def.max}
            value={disp}
            onChange={(e) => handle(Number(e.target.value))}
            className="w-24 rounded-lg border border-[var(--line)] bg-[var(--paper-2)] px-2.5 py-1.5 text-right font-mono2 text-sm font-semibold text-[var(--ink)] outline-none transition-colors duration-200 focus:border-[var(--brand)]"
          />
          {def.unit && <span className="w-9 text-xs text-[var(--muted)]">{def.unit}</span>}
        </div>
      </div>
      <input
        data-testid={`calc-field-${def.key}-slider`}
        type="range"
        min={def.min}
        max={def.max}
        value={disp}
        onChange={(e) => handle(Number(e.target.value))}
        className="calc-range"
        style={{ "--fill": `${fill}%` }}
        aria-label={def.label}
      />
    </div>
  );
};

export default function Calculator() {
  const [showHow, setShowHow] = useState(false);
  const [v, setV] = useState({ appts: 12, minutes: 6, days: 5, cost: 25 });

  const setField = (key, raw) => setV((prev) => ({ ...prev, [key]: raw }));

  const weeklyHours = (v.appts * v.minutes * v.days) / 60;
  const monthlyHours = weeklyHours * WEEKS_MONTH;
  const yearlyHours = weeklyHours * 52;
  const saveM = monthlyHours * v.cost;
  const saveY = saveM * 12;
  const recovered = Math.round(v.appts * v.days * WEEKS_MONTH * 0.15);

  const aSaveM = useAnimatedNumber(saveM);
  const aSaveY = useAnimatedNumber(saveY);
  const aHoursW = useAnimatedNumber(weeklyHours);
  const aHoursM = useAnimatedNumber(monthlyHours);
  const aHoursY = useAnimatedNumber(yearlyHours);
  const aRecovered = useAnimatedNumber(recovered);

  const highVolume = v.appts > 150;

  return (
    <section id="calculadora" data-testid="calculator-section" className="relative py-28 sm:py-36">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <ChapterHeading
          index="03"
          eyebrow="Calculadora de economia"
          lines={["Quanto sua agenda pode", "estar deixando na mesa?"]}
          sub="Ajuste os dados da sua operação e veja estimativas em tempo real. Nenhum resultado aqui é garantia — é uma projeção baseada nos dados que você inserir."
        />

        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          {/* INPUTS */}
          <Reveal className="rounded-3xl border border-[var(--line)] bg-[var(--card)] p-8 sm:p-10">
            <p className="mb-9 font-mono2 text-[11px] uppercase tracking-[0.3em] text-[var(--brand)]">Sua operação hoje</p>
            <div className="space-y-9">
              {FIELDS.map((f) => (
                <Field key={f.key} def={f} value={v[f.key]} onChange={setField} />
              ))}
            </div>
            <AnimatePresence>
              {highVolume && (
                <motion.p
                  data-testid="calc-warning-0"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="mt-8 flex items-start gap-2.5 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-700 dark:text-amber-300"
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  Volume alto para uma estimativa automática — na demonstração fazemos um estudo personalizado para a sua operação.
                </motion.p>
              )}
            </AnimatePresence>
          </Reveal>

          {/* RESULTADOS */}
          <Reveal delay={0.1}>
            <div className="grain sticky top-24 overflow-hidden rounded-3xl bg-petrol p-8 text-white sm:p-10">
              <div className="relative z-10">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-turquoise">Com a CliniAtiva</p>
                  <button
                    data-testid="calc-how-button"
                    onClick={() => setShowHow(true)}
                    className="flex items-center gap-1.5 rounded-full border border-white/20 px-3.5 py-1.5 text-xs font-medium text-sage transition-colors duration-300 hover:border-turquoise hover:text-turquoise"
                  >
                    <Info className="h-3.5 w-3.5" /> Como calculamos
                  </button>
                </div>

                <div className="mt-8">
                  <p className="text-sm text-sage/80">Economia estimada por mês</p>
                  <p data-testid="calc-res-save-month" className="mt-1 break-words font-display text-5xl font-extrabold tracking-tight text-turquoise sm:text-6xl">
                    {brl.format(aSaveM)}
                  </p>
                  <p className="mt-1 text-xs text-sage/50">estimativa — não é garantia de resultado</p>
                </div>

                <div className="mt-9 grid grid-cols-3 gap-4 border-t border-white/10 pt-8">
                  <div data-testid="calc-res-hours-week">
                    <p className="font-display text-2xl font-extrabold text-white sm:text-3xl">{num.format(aHoursW)}h</p>
                    <p className="mt-1 text-[11px] uppercase tracking-wider text-sage/70">por semana</p>
                  </div>
                  <div data-testid="calc-res-hours-month">
                    <p className="font-display text-2xl font-extrabold text-white sm:text-3xl">{num.format(aHoursM)}h</p>
                    <p className="mt-1 text-[11px] uppercase tracking-wider text-sage/70">por mês</p>
                  </div>
                  <div data-testid="calc-res-hours-year">
                    <p className="font-display text-2xl font-extrabold text-white sm:text-3xl">{num.format(aHoursY)}h</p>
                    <p className="mt-1 text-[11px] uppercase tracking-wider text-sage/70">por ano</p>
                  </div>
                </div>

                <div className="mt-7 space-y-3">
                  <div className="flex items-center justify-between rounded-2xl bg-turquoise/10 px-5 py-4">
                    <span className="text-sm text-sage">Economia estimada por ano</span>
                    <span data-testid="calc-res-save-year" className="font-display text-lg font-extrabold text-white">{brl.format(aSaveY)}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-2xl bg-white/[0.05] px-5 py-4">
                    <span className="text-sm text-sage">Agendamentos recuperados por mês*</span>
                    <span data-testid="calc-res-appts" className="font-display text-lg font-extrabold text-white">{num.format(aRecovered)}</span>
                  </div>
                </div>

                <p className="mt-5 text-[11px] leading-relaxed text-sage/50">
                  *Estimativa com redução de faltas por confirmação automática. As projeções dependem dos dados inseridos,
                  do fluxo de atendimento e do nível de automação possível no seu negócio.
                </p>

                <motion.button
                  data-testid="calc-cta-demo"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => scrollToId("demonstracao")}
                  className="mt-7 w-full rounded-full bg-turquoise py-4 font-display text-base font-bold text-petrol transition-shadow duration-300 hover:shadow-[0_12px_36px_rgba(39,179,176,0.45)]"
                >
                  Quero economizar isso — agendar demonstração
                </motion.button>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* MODAL COMO CALCULAMOS */}
      <AnimatePresence>
        {showHow && (
          <motion.div
            data-testid="calc-how-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowHow(false)}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-navy/70 p-4 backdrop-blur-sm"
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[78vh] w-full max-w-2xl overflow-y-auto rounded-[28px] bg-[var(--card)] p-8 text-[var(--ink)] shadow-2xl sm:p-10"
            >
              <div className="flex items-start justify-between gap-6">
                <h3 className="font-display text-2xl font-bold">Como calculamos</h3>
                <button
                  data-testid="calc-how-close"
                  onClick={() => setShowHow(false)}
                  aria-label="Fechar metodologia"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--line)] transition-colors duration-300 hover:border-[var(--brand)] hover:text-[var(--brand)]"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <ul className="mt-6 list-disc space-y-3 pl-5 text-sm leading-relaxed text-[var(--muted)]">
                <li>Consideramos 4,33 semanas por mês.</li>
                <li>Horas economizadas = agendamentos × tempo médio por agendamento × dias de funcionamento — o tempo que hoje a sua equipe gasta no WhatsApp marcando, confirmando e remarcando.</li>
                <li>Economia financeira = horas economizadas × custo da hora da equipe.</li>
                <li>Agendamentos recuperados = agendamentos do mês × 15% — uma referência conservadora de faltas evitadas com confirmações automáticas.</li>
              </ul>
              <p className="mt-6 rounded-2xl border border-[var(--brand)]/30 bg-[var(--brand)]/5 p-4 text-sm leading-relaxed text-[var(--ink)]">
                Todos os resultados são <strong>estimativas ilustrativas</strong>: dependem dos dados inseridos, do fluxo
                de atendimento, da taxa de faltas e do nível de automação possível. Não constituem garantia de economia,
                receita ou agendamentos.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
